// src/pages/AgentLogin.js
// HomeIQa — Agent portal login (Google only)
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signOut
} from 'firebase/auth';
import { auth } from '../firebase';
import Logo from '../components/Logo';
import { provisionAgent } from '../lib/auth';
import { IDLE_FLAG } from '../components/agent/IdleTimeout';

export default function AgentLogin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(IDLE_FLAG) === '1') {
      setSessionExpired(true);
    }
  }, []);

  const dismissExpired = () => {
    sessionStorage.removeItem(IDLE_FLAG);
    setSessionExpired(false);
  };

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(() => {});
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const profile = await provisionAgent(u);
          if (profile?.status === 'active') navigate('/agent/dashboard', { replace: true });
          else navigate('/agent/pending', { replace: true });
        } catch (e) {
          console.error('provisionAgent failed', e);
          if (e.message === 'UNAUTHORIZED_EMAIL') {
            // Strict gate: not admin, no invite. Sign them out and show a clear rejection.
            await signOut(auth).catch(() => {});
            setUser(null);
            setError(`Access restricted. The email ${u.email} is not authorized for HomeIQa. Please contact your admin to be invited.`);
          } else {
            setError(`Account setup error: ${e.message}.`);
          }
        }
      }
    });
    return unsub;
  }, [navigate]);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        setError(err.message || 'Sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetAndSignIn = async () => {
    dismissExpired();
    await handleGoogleSignIn();
  };


  return (
    <div style={s.page}>
      <div style={s.bgGlow} />
      <div style={s.bgGlow2} />

      <div style={s.topBar}>
        <Logo />
        <button style={s.backBtn} onClick={() => navigate('/')}>← Back to home</button>
      </div>

      <main style={s.main}>
        <div style={s.card}>
          <div style={s.cardHeader}>
            <div style={s.eyebrow}>Agent Portal</div>
            <h1 style={s.h1}>
              Welcome <em style={s.em}>back</em>
            </h1>
            <p style={s.sub}>
              Sign in to manage your listings, client reports, and appraiser referrals.
            </p>
          </div>

          {!user ? (
            <>
              <button
                style={{ ...s.googleBtn, opacity: loading ? 0.7 : 1, cursor: loading ? 'wait' : 'pointer' }}
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <GoogleIcon />
                <span>{loading ? 'Signing you in…' : 'Continue with Google'}</span>
              </button>

              {error && <div style={s.error}>{error}</div>}

              <div style={s.divider}>
                <span style={s.dividerLine} />
                <span style={s.dividerText}>Verified agents only</span>
                <span style={s.dividerLine} />
              </div>

              <p style={s.fineprint}>
                By continuing, you agree to our{' '}
                <a href="/terms" style={s.link}>Terms of Service</a> and{' '}
                <a href="/privacy" style={s.link}>Privacy Policy</a>.
              </p>
            </>
          ) : (
            <div style={s.welcomeBlock}>
              {user.photoURL && <img src={user.photoURL} alt="" style={s.avatar} referrerPolicy="no-referrer" />}
              <div style={s.welcomeName}>{user.displayName || 'Agent'}</div>
              <div style={s.welcomeEmail}>{user.email}</div>
              <p style={s.welcomeNote}>Redirecting to your dashboard…</p>
              {error && <div style={s.error}>{error}</div>}
              <button style={s.primaryBtn} onClick={() => navigate('/agent/dashboard', { replace: true })}>
                Open dashboard now
              </button>
            </div>
          )}
        </div>

        <div style={s.aside}>
          <div style={s.asideEyebrow}>For agents</div>
          <h2 style={s.asideH2}>
            A smarter way to <em style={s.asideEm}>price</em>, <em style={s.asideEm}>pitch</em>, and <em style={s.asideEm}>close</em>.
          </h2>
          <ul style={s.asideList}>
            <li style={s.asideItem}><span style={s.asideDot} />Generate client-ready reports in under 2 minutes</li>
            <li style={s.asideItem}><span style={s.asideDot} />Government records + live MLS comps in one place</li>
            <li style={s.asideItem}><span style={s.asideDot} />Refer official appraisals when needed</li>
            <li style={s.asideItem}><span style={s.asideDot} />White-label PDF delivery to your clients</li>
          </ul>
          <div style={s.asideFootnote}>
            Not an agent yet?{' '}
            <a href="/appraisers/join" style={s.asideLink}>Apply to join the directory →</a>
          </div>
        </div>
      </main>

      {sessionExpired && (
        <div style={x.overlay} role="dialog" aria-modal="true">
          <div style={x.modal}>
            <div style={x.eyebrow}>Session expired</div>
            <h2 style={x.title}>You've been <em style={x.em}>signed out</em>.</h2>
            <p style={x.body}>
              For your security, your session ended after 20 minutes of inactivity.
              Click below to reset the timer and sign back in.
            </p>
            <button onClick={handleResetAndSignIn} disabled={loading} style={x.btn}>
              {loading ? 'Signing in…' : 'Reset timer & sign back in'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const x = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(74,18,32,0.78)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(2px)', fontFamily: "'DM Sans', sans-serif" },
  modal: { background: '#fff', borderRadius: 10, padding: '32px 36px', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.5)', border: '1px solid #C9A84C' },
  eyebrow: { fontSize: 10, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C9A84C', borderBottom: '1px solid #C9A84C', paddingBottom: 4, display: 'inline-block', marginBottom: 14 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 500, color: '#4A1220', lineHeight: 1.2, margin: '0 0 18px' },
  em: { fontStyle: 'italic', color: '#6B1E2E' },
  body: { fontSize: 13, color: '#5A5A5A', lineHeight: 1.6, marginBottom: 22 },
  btn: { background: '#6B1E2E', color: '#fff', border: 'none', borderRadius: 4, padding: '13px 28px', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", width: '100%' }
};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.71v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.61z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.36 0-4.36-1.59-5.07-3.74H.96v2.34A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.93 10.68A5.4 5.4 0 0 1 3.64 9c0-.58.1-1.15.29-1.68V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l2.97-2.36z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.96l2.97 2.36C4.64 5.17 6.64 3.58 9 3.58z" />
    </svg>
  );
}

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const GOLD_LITE = '#E2C47A';
const CREAM = '#FAF7F2';
const WHITE = '#FFFFFF';
const MID = '#5A5A5A';
const LIGHT = '#9A9A9A';
const BORDER = 'rgba(107,30,46,0.1)';

const s = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${BURG_DK} 0%, ${BURG} 100%)`,
    fontFamily: "'DM Sans', sans-serif",
    position: 'relative',
    overflow: 'hidden',
    paddingBottom: 80
  },
  bgGlow: {
    position: 'absolute', top: '-180px', right: '-180px',
    width: 480, height: 480, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(201,168,76,0.18) 0%, rgba(201,168,76,0) 70%)',
    pointerEvents: 'none'
  },
  bgGlow2: {
    position: 'absolute', bottom: '-220px', left: '-220px',
    width: 520, height: 520, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(226,196,122,0.12) 0%, rgba(226,196,122,0) 70%)',
    pointerEvents: 'none'
  },
  topBar: {
    position: 'relative', zIndex: 2,
    padding: '24px 48px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
  },
  backBtn: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 4,
    padding: '8px 16px',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif"
  },
  main: {
    position: 'relative', zIndex: 2,
    maxWidth: 1080, margin: '0 auto', padding: '40px 24px',
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'center'
  },
  card: {
    background: WHITE,
    borderRadius: 10,
    padding: '48px 44px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.4)'
  },
  cardHeader: { marginBottom: 28 },
  eyebrow: {
    fontSize: 10, fontWeight: 500, letterSpacing: '0.14em',
    textTransform: 'uppercase', color: GOLD,
    borderBottom: `1px solid ${GOLD}`, paddingBottom: 4,
    display: 'inline-block', marginBottom: 18
  },
  h1: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 38, fontWeight: 500,
    color: BURG_DK, lineHeight: 1.15, letterSpacing: '-0.4px',
    marginBottom: 12
  },
  em: { fontStyle: 'italic', color: BURG },
  sub: {
    fontSize: 14, fontWeight: 300, color: MID, lineHeight: 1.6
  },
  googleBtn: {
    width: '100%',
    background: WHITE,
    color: '#1C1C1C',
    border: `1px solid ${BORDER}`,
    borderRadius: 4,
    padding: '14px 18px',
    fontSize: 14, fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
  },
  error: {
    marginTop: 14,
    padding: '10px 14px',
    background: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: 4,
    color: '#991B1B',
    fontSize: 12,
    lineHeight: 1.5
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: 12, margin: '28px 0 16px'
  },
  dividerLine: { flex: 1, height: 1, background: BORDER },
  dividerText: {
    fontSize: 10, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: LIGHT
  },
  fineprint: {
    fontSize: 11, color: LIGHT, lineHeight: 1.6, textAlign: 'center'
  },
  link: { color: BURG, textDecoration: 'underline' },
  welcomeBlock: { textAlign: 'center', padding: '8px 0' },
  avatar: {
    width: 72, height: 72, borderRadius: '50%',
    border: `2px solid ${GOLD}`, marginBottom: 14
  },
  welcomeName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 24, fontWeight: 500, color: BURG_DK
  },
  welcomeEmail: { fontSize: 12, color: LIGHT, marginBottom: 18 },
  welcomeNote: {
    fontSize: 13, color: MID, lineHeight: 1.6,
    background: CREAM, padding: '14px 16px', borderRadius: 4,
    borderLeft: `3px solid ${GOLD}`, textAlign: 'left',
    marginBottom: 20
  },
  primaryBtn: {
    width: '100%', background: BURG, color: WHITE,
    border: 'none', borderRadius: 4, padding: '13px',
    fontSize: 14, fontWeight: 500, cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", marginBottom: 10
  },
  aside: { color: WHITE, paddingLeft: 8 },
  asideEyebrow: {
    fontSize: 10, fontWeight: 500, letterSpacing: '0.14em',
    textTransform: 'uppercase', color: GOLD_LITE,
    marginBottom: 16
  },
  asideH2: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 38, fontWeight: 500, lineHeight: 1.18,
    letterSpacing: '-0.5px', marginBottom: 28
  },
  asideEm: { fontStyle: 'italic', color: GOLD_LITE },
  asideList: { listStyle: 'none', margin: 0, padding: 0, marginBottom: 28 },
  asideItem: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '10px 0', fontSize: 14, color: 'rgba(255,255,255,0.78)',
    fontWeight: 300, lineHeight: 1.5
  },
  asideDot: {
    width: 6, height: 6, borderRadius: '50%',
    background: GOLD, marginTop: 8, flexShrink: 0
  },
  asideFootnote: {
    fontSize: 12, color: 'rgba(255,255,255,0.5)',
    paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.1)'
  },
  asideLink: {
    color: GOLD_LITE, textDecoration: 'underline',
    textDecorationColor: 'rgba(226,196,122,0.4)'
  }
};
